/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { HelpOutlineTwoTone } from "@mui/icons-material";
import { Box, FormControl, InputLabel, MenuItem, Select, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useCloud } from "../Hooks/Cloud";
import { useRegion } from "../Hooks/Region";
import "flag-icons/css/flag-icons.min.css";

/**
 * Region picker component
 * Description: This component is used to select the region to be used in the application.
 */
function RegionPicker() {
  const { cloud } = useCloud();
  const { region, setRegion } = useRegion();
  const [ geography, setGeography ] = useState();

  // Check if the cloud key exists in window.ENV.regions
  const regions = window.ENV[cloud].regions || [];
  const regionHelperTip = window.ENV[cloud].regionHelperTip;
  const isDisabled = regions.length === 1 ? true : false;
  const pickerLabel = window.ENV[cloud].regionPickerLabel || "Region";
  const regionName = window.ENV[cloud].regionName || {};

  const geographies = window.ENV[cloud].geographies || [];
  const geographyHelperTip = window.ENV[cloud].geographyHelperTip;
  const isDisabledGeo = geographies.length === 1 ? true : false;
  const geographyPickerLabel = window.ENV[cloud].geographyPickerLabel || "Geography";
  const geographyName = window.ENV[cloud].geographyName || {};
  const geographyHosts = window.ENV[cloud].geographyHosts || {};

  const handleGeographyChange = (event) => {
    // window.href = geographyHosts[event] || window.location.href;
    window.open(new URL(geographyHosts[event] || window.location.href), "_self");
  };

  useEffect(() => {
    // check for hostname, and do a reverse config search to put the correct geography in the selector
    if (geographies.length > 0) {
      const currentHost = window.location.hostname;

      for (const geo of geographies) {
        if (geographyHosts[geo] && (new URL(geographyHosts[geo])).hostname === currentHost) {
          setGeography(geo);

          break;
        }
      }
    }
  }, []);

  return (
    <>
      { ( geographies && geographies.length > 0 ) && (
        <FormControl sx={ { minWidth: 60, my: 1 } } size="small">
          <InputLabel id="geography-picker-label">{ geographyPickerLabel }</InputLabel>
          <Select
            id="geography-picker"
            labelId="geography-picker-label"
            label={ geographyPickerLabel }
            value={ geography || geographies[0] }
            onChange={ (event) => handleGeographyChange(event.target.value) }
            disabled={ isDisabledGeo }
          >
            { geographies &&
              geographies.map((g) => {
                return (
                  <MenuItem key={ g } value={ g } >
                    <Box component="span" sx={ { alignItems: "center", display: "flex" } }>
                      <Box component="span" className={ `fi fi-${g} fis` } sx={ { borderRadius: "50%", mr: 1 } } />
                      <Box component="span" sx={ { pr: 1 } }>
                        { geographyName[g] ||  g.toUpperCase() }
                      </Box>

                      { /* Tooltip component to show the description of the geography */ }
                      { geographyHelperTip && geographyHelperTip[g] && (
                        <Tooltip title={ geographyHelperTip[g] } arrow style={ { cursor: "pointer" } }>
                          <HelpOutlineTwoTone color="disabled" fontSize="inherit" />
                        </Tooltip>
                      ) }
                    </Box>
                  </MenuItem>
                );
              }) }
          </Select>
        </FormControl>
      ) }

      <FormControl sx={ { minWidth: 120, ml: 1, my: 1 } } size="small">
        <InputLabel id="region-picker-label">{ pickerLabel }</InputLabel>
        <Select
          id="region-picker"
          labelId="region-picker-label"
          label={ pickerLabel }
          value={ region }
          onChange={ (event) => setRegion(event.target.value) }
          disabled={ isDisabled }
        >
          { regions &&
            regions.map((r) => {
              return (
                <MenuItem key={ r } value={ r } >
                  <Box component="span" sx={ { alignItems: "center", display: "flex" } }>
                    <Box component="span" sx={ { pr: 1 } }>
                      { regionName[r] || r.toUpperCase() }
                    </Box>

                    { /* Tooltip component to show the description of the region */ }
                    { regionHelperTip && regionHelperTip[r] && (
                      <Tooltip title={ regionHelperTip[r] } arrow style={ { cursor: "pointer" } }>
                        <HelpOutlineTwoTone color="disabled" fontSize="inherit" />
                      </Tooltip>
                    ) }
                  </Box>
                </MenuItem>
              );
            }) }
        </Select>
      </FormControl>
    </>
  );
}

export default RegionPicker;
